<template>
  <div>
    <Breadcrumb
      v-if="currentStepIndex > 0 && !isEditStep"
      :header="backButtonLabel"
      :isButton="true"
      class="mt-16 mb-24 md:mt-0"
      @click="navigateToPreviousStep"
    />
    <div :key="draftDataCacheKey">
      <keep-alive>
        <router-view :stepsData="stepsData" :draft="draftData" />
      </keep-alive>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { RouteConfig } from 'vue-router';
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';

interface RouteStepperData {
  stepsData: Record<string, any>[];
  draftData: Record<string, any> | null;
  draftDataCacheKey: string;
  registeredRouteStepperNextButton: Vue.Component | null;
}

export default {
  name: 'RouteStepper',
  components: { Breadcrumb },
  provide() {
    return {
      navigateToNextStep: this.navigateToNextStep,
    };
  },
  model: {
    prop: 'modelValue',
    event: 'input',
  },
  props: {
    modelValue: {
      type: Object as PropType<Record<string, any> | null>,
      default: () => ({}),
    },
    stepFilter: {
      type: Function,
      default: null,
    },
  },
  data(): RouteStepperData {
    return {
      stepsData: [],
      draftData: this.modelValue,
      draftDataCacheKey: JSON.stringify(this.modelValue),
      registeredRouteStepperNextButton: null,
    };
  },
  computed: {
    backButtonLabel(): string {
      return this.previousStep?.meta?.stepLabel || 'Previous Step';
    },
    steps(): RouteConfig[] {
      const parentPath = this.$route?.matched?.[0]?.path ?? null;
      const parentRoute = this.$router?.options?.routes?.find((route) => route.path === parentPath);

      if (!parentRoute?.children) {
        // eslint-disable-next-line no-console
        console.error('No children routes found within parent route');
        return [];
      }

      let steps = parentRoute?.children?.filter((child: any) => !!child.component);

      if (typeof this.stepFilter === 'function') {
        steps = steps.filter((step) => this.stepFilter(step));
      }

      return steps;
    },
    currentStepIndex(): number {
      const currentStepIndex = this.steps.findIndex((step) => step.name === this.$route.name);
      if (currentStepIndex === -1) {
        // eslint-disable-next-line no-console
        console.error('Current step not found within steps');
        return 0; // default to the first step if not found
      }
      return currentStepIndex;
    },
    isEditStep(): boolean {
      return this.$route.query.mode === 'edit';
    },
    nextStep(): RouteConfig | null {
      return this.steps[this.currentStepIndex + 1] || null;
    },
    previousStep(): RouteConfig | null {
      return this.steps[this.currentStepIndex - 1] || null;
    },
    returnPath(): string | null {
      const returnPath: string | null = this.$route.query.returnPath as string | null;
      return returnPath;
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler() {
        this.$emit('progress', {
          // emit up the current step index and total steps for progress tracking
          currentStep: this.currentStepIndex + 1,
          totalSteps: this.steps.length,
        });
      },
    },
    modelValue: {
      deep: true,
      handler(modelValue: Record<string, any> | null): void {
        // if the parent and child draft get out of sync, then reset the cache and rerender
        const modelValueStr = JSON.stringify(this.modelValue);
        const draftDataStr = JSON.stringify(this.draftData);

        if (modelValueStr !== draftDataStr) {
          this.draftData = modelValue;
          this.draftDataCacheKey = modelValueStr;

          this.resubmitEachStep();
        }
      },
    },
  },
  created() {
    this.navigateToFirstIncompleteOrLastStep();
    this.$root.$on('register-next-button', (instance) => {
      this.registeredRouteStepperNextButton = instance;
    });
  },
  beforeDestroy() {
    this.stepsData = [];
  },
  methods: {
    async resubmitEachStep() {
      // go through each step and submit
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.steps.length; i++) {
        /* eslint-disable-next-line no-await-in-loop */
        await this.$nextTick();
        if (this.registeredRouteStepperNextButton === null) {
          // if no next button is registered, break out of the loop
          break;
        }

        const curStepValid: boolean = this.registeredRouteStepperNextButton?.submitStep();
        // if the current step doesn't pass validation, break out of the loop so the user can continue from there
        if (!curStepValid) break;

        this.registeredRouteStepperNextButton = null;
      }
    },
    navigateToFirstIncompleteOrLastStep(): void {
      const firstIncompleteStep = this.steps.find((_, index) => {
        return !this.stepsData[index];
      });
      const targetStep = firstIncompleteStep || this.steps[this.steps.length - 1]; // revert to final step if all are complete

      if (targetStep === this.steps[this.currentStepIndex]) return; // do not navigate if already on the target step

      this.$router.push(targetStep.path);
    },
    saveStepData(newStepData: Record<string, any> = {}): void {
      const newStepsData = [...this.stepsData];
      newStepsData[this.currentStepIndex] = {
        ...newStepsData[this.currentStepIndex],
        ...newStepData,
      };
      this.stepsData = newStepsData;
    },
    saveDraftData(newDraftData: Record<string, any>): void {
      this.draftData = { ...this.draftData, ...newDraftData };
      this.$emit('input', this.draftData);
    },
    navigateToPreviousStep(): void {
      if (!this.previousStep) {
        // eslint-disable-next-line no-console
        console.error('No previous step found');
        return;
      }
      this.$router.push(this.previousStep.path);
    },
    navigateToNextStep(newStepData?: Record<string, any>, newDraftData: Record<string, any> = {}): void {
      if (!this.nextStep) {
        // eslint-disable-next-line no-console
        console.error('No next step found');
        return;
      }

      this.saveStepData(newStepData);
      this.saveDraftData(newDraftData);

      // if step is edit, return to the review page
      if (this.isEditStep && this.returnPath) {
        this.$router.push(this.returnPath);
        return;
      }

      this.$router.push({ path: this.nextStep.path });
    },
  },
};
</script>
